import { segmentedGroup, segment, segmentSelected } from './segmented-control.css';

export interface SegmentedOption<T extends string> {
    value: T;
    label: string;
    /** Disable this single segment (e.g. an already-reached rollout stage). */
    disabled?: boolean;
}

export interface SegmentedControlProps<T extends string> {
    options: readonly SegmentedOption<T>[];
    value: T;
    onChange: (value: T) => void;
    /** Accessible name for the whole group (radiogroup requires one). */
    accessibilityLabel: string;
    /** Disable every segment (e.g. while a mutation is in flight). */
    disabled?: boolean;
    testID?: string;
}

/**
 * Untitled UI segmented control — a single-select pill track. Rendered as an
 * ARIA `radiogroup` of `radio` buttons so assistive tech announces it as one
 * choice. No fixed height: segments size to their (scalable) text.
 */
export function SegmentedControl<T extends string>({
    options,
    value,
    onChange,
    accessibilityLabel,
    disabled,
    testID,
}: SegmentedControlProps<T>) {
    return (
        <div className={segmentedGroup} role="radiogroup" aria-label={accessibilityLabel} data-testid={testID}>
            {options.map((option) => {
                const selected = option.value === value;
                return (
                    <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        className={`${segment} ${selected ? segmentSelected : ''}`}
                        disabled={disabled || option.disabled}
                        onClick={() => {
                            if (!selected) onChange(option.value);
                        }}
                    >
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}
